import { useSearchParams } from 'react-router-dom'
import { Suspense, useState } from 'react'
import { lazyComRetry } from '@/lazyComRetry'
import { Stack, Tabs, Text, Loader, Center, Skeleton } from '@mantine/core'
import { IconEye, IconBulb, IconChartLine, IconGauge, IconActivity, IconTerminal2, IconSchema } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { databaseApi } from '@/api/database'
import { awsAccessApi } from '@/api/awsAccess'
import { NoRdsEmptyState } from '@/components/database/NoRdsEmptyState'
import { OverviewTab } from './OverviewTab'
import { PerformanceTab } from './PerformanceTab'
import { RecommendationsTab } from './RecommendationsTab'
import { CapacityTab } from './CapacityTab'
import { MetricsTab } from './MetricsTab'

const QueryRunnerTab = lazyComRetry(() =>
  import('./QueryRunnerTab').then((m) => ({ default: m.QueryRunnerTab })),
)

const ErDiagramTab = lazyComRetry(() =>
  import('./ErDiagramTab').then((m) => ({ default: m.ErDiagramTab })),
)

/** Os valores que <Tabs.Tab> aceita. Parametro fora daqui cai em 'overview' — melhor que
 * tela vazia, que e o que acontece quando o Tabs recebe um value desconhecido. */
const ABAS_VALIDAS = ['overview', 'recommendations', 'capacity', 'metrics', 'performance', 'query', 'er-diagram'] as string[]

export function DatabasePage() {
  // A aba vive na URL, e nao em useState: assim ela e linkavel, sobrevive ao F5, e verificacao
  // automatizada alcanca cada uma sem precisar clicar. Padrao ja usado em WafDetail e
  // AuditoriaGlobal.
  const [params, setParams] = useSearchParams()
  const pedida = params.get('aba')
  const activeTab = ABAS_VALIDAS.includes(pedida ?? '') ? (pedida as string) : 'overview'

  const setActiveTab = (valor: string | null) => {
    const proxima = new URLSearchParams(params)
    if (!valor || valor === 'overview') proxima.delete('aba')
    else proxima.set('aba', valor)
    // replace: trocar de aba nao e navegacao — o voltar do navegador deve sair da tela, e nao
    // desfazer clique de aba.
    setParams(proxima, { replace: true })
  }
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const queryClient = useQueryClient()

  const instancesQuery = useQuery({
    queryKey: ['rds-instances', tenantId],
    queryFn: () => databaseApi.listInstances(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
    retry: false,
  })

  const awsAccessQuery = useQuery({
    queryKey: ['aws-access-status', tenantId],
    queryFn: () => awsAccessApi.getStatus(tenantId!),
    enabled: !!tenantId && (instancesQuery.isError || (instancesQuery.isSuccess && (instancesQuery.data?.items?.length ?? 0) === 0)),
    staleTime: 120_000,
    retry: false,
  })

  const diagnoseMutation = useMutation({
    mutationFn: () => awsAccessApi.diagnose(tenantId!),
    onSuccess: (data) => {
      queryClient.setQueryData(['aws-access-status', tenantId], data)
      instancesQuery.refetch()
    },
  })

  const hasInstances = (instancesQuery.data?.items?.length ?? 0) > 0
  const showEmpty =
    !!tenantId &&
    !instancesQuery.isLoading &&
    (instancesQuery.isError || (instancesQuery.isSuccess && !hasInstances))

  const accessStatus = awsAccessQuery.data?.overall_status
  const firstRegionError = awsAccessQuery.data?.regions.find(
    (r) => r.status !== 'healthy',
  )
  const accessError = firstRegionError?.error_message ?? firstRegionError?.error_code ?? null

  return (
    <Stack gap="xl">
      {/* O titulo vem do layout desde 2026-08-13 (routeHeader + PageHeader). Ele estava aqui
          tambem, e por isso "Banco de Dados" aparecia tres vezes empilhado: barra do topo,
          trilha e este. Foi o achado que iniciou a refatoracao do cabecalho. */}

      {!tenantId ? (
        <Text c="dimmed">Selecione um tenant para visualizar instâncias RDS</Text>
      ) : instancesQuery.isLoading ? (
        <Stack gap="md">
          <Skeleton height={36} width={420} />
          <Skeleton height={120} radius="md" />
        </Stack>
      ) : showEmpty ? (
        <NoRdsEmptyState
          tenantName={tenant?.name}
          onRetry={() => instancesQuery.refetch()}
          awsAccessStatus={accessStatus}
          awsAccessError={accessError}
          onDiagnose={() => diagnoseMutation.mutate()}
        />
      ) : (
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconEye size={16} />}>
              Visão Geral
            </Tabs.Tab>
            <Tabs.Tab value="recommendations" leftSection={<IconBulb size={16} />}>
              Recomendações
            </Tabs.Tab>
            <Tabs.Tab value="capacity" leftSection={<IconChartLine size={16} />}>
              Capacidade
            </Tabs.Tab>
            <Tabs.Tab value="metrics" leftSection={<IconGauge size={16} />}>
              Métricas
            </Tabs.Tab>
            <Tabs.Tab value="performance" leftSection={<IconActivity size={16} />}>
              Performance
            </Tabs.Tab>
            <Tabs.Tab value="query" leftSection={<IconTerminal2 size={16} />}>
              Query Runner
            </Tabs.Tab>
            <Tabs.Tab value="er-diagram" leftSection={<IconSchema size={16} />}>
              Diagrama MER
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <OverviewTab />
          </Tabs.Panel>
          <Tabs.Panel value="recommendations" pt="md">
            <RecommendationsTab />
          </Tabs.Panel>
          <Tabs.Panel value="capacity" pt="md">
            <CapacityTab />
          </Tabs.Panel>
          <Tabs.Panel value="metrics" pt="md">
            <MetricsTab />
          </Tabs.Panel>
          <Tabs.Panel value="performance" pt="md">
            <PerformanceTab />
          </Tabs.Panel>
          <Tabs.Panel value="query" pt="md">
            <Suspense fallback={<Center py="xl"><Loader /></Center>}>
              <QueryRunnerTab />
            </Suspense>
          </Tabs.Panel>
          <Tabs.Panel value="er-diagram" pt="md">
            <Suspense fallback={<Center py="xl"><Loader /></Center>}>
              <ErDiagramTab />
            </Suspense>
          </Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  )
}
